import { type ReactNode } from 'react';
import { BonusHistoryModal } from './bonus-history-modal';
import { useGroupHistory, type GroupWeek } from '../hooks/use-bonus-history';
import { formatUSD } from '../utils/payment-formatters';

type Props = { open: boolean; onClose: () => void };

const TH =
  'pb-3 text-left text-[10px] font-medium uppercase tracking-wider text-text-tertiary/60';
const TD = 'py-3 align-middle';

function fmtDate(d: string): string {
  return d.slice(5, 10).replace('-', '/');
}

function pct(total: number, goal: number): number {
  if (goal <= 0) return 0;
  return Math.min(100, Math.round((total / goal) * 100));
}

function TeamCell({ total, goal }: { total: number; goal: number }): ReactNode {
  const reached = total >= goal && goal > 0;
  return (
    <div className="space-y-1.5">
      <p className="font-mono text-[11px] tabular-nums text-text-secondary">
        <span className={reached ? 'text-text-primary' : ''}>{total}</span>
        <span className="text-text-tertiary/60"> / {goal}</span>
      </p>
      <div className="h-[3px] overflow-hidden rounded-full bg-white/[0.05]">
        <div
          className="h-full rounded-full bg-brand-red/70"
          style={{ width: `${pct(total, goal)}%` }}
        />
      </div>
    </div>
  );
}

function YourCell({ amount }: { amount: number }): ReactNode {
  if (amount > 0) {
    return (
      <span className="font-mono text-xs font-medium tabular-nums text-text-primary">
        +{formatUSD(amount)}
      </span>
    );
  }
  return (
    <span className="text-[11px] text-text-tertiary/60">
      No alcanzaste tramo
    </span>
  );
}

function Row({ w }: { w: GroupWeek }): ReactNode {
  return (
    <tr className="border-t border-white/[0.06]">
      <td className={`${TD} pr-md text-[11px] tabular-nums text-text-tertiary`}>
        {fmtDate(w.week_start)}–{fmtDate(w.week_end)}
      </td>
      <td className={`${TD} w-[140px] pr-md`}>
        <TeamCell total={w.week_total} goal={w.week_goal} />
      </td>
      <td className={TD}>
        <YourCell amount={w.your_amount} />
      </td>
    </tr>
  );
}

function Table({ weeks }: { weeks: GroupWeek[] }): ReactNode {
  return (
    <table className="w-full min-w-[28rem] text-left">
      <thead>
        <tr>
          <th className={TH}>Semana</th>
          <th className={TH}>Equipo</th>
          <th className={TH}>Tu monto</th>
        </tr>
      </thead>
      <tbody>
        {weeks.map((w) => (
          <Row key={w.week_start} w={w} />
        ))}
      </tbody>
    </table>
  );
}

export function GroupHistoryModal(p: Props): ReactNode {
  const { data, isLoading } = useGroupHistory(p.open);
  const weeks = data?.weeks ?? [];
  return (
    <BonusHistoryModal
      open={p.open}
      onClose={p.onClose}
      title="Histórico Bonus de equipo"
      loading={isLoading}
      empty={!isLoading && weeks.length === 0}
      emptyLabel="El equipo aún no ha desbloqueado el bonus"
    >
      <Table weeks={weeks} />
    </BonusHistoryModal>
  );
}
